<template>
  <span
    class="json-example-input-wrap"
    :data-json-example-input="inputKey"
  >
    <AntInput
      ref="inputRef"
      :placeholder="placeholder"
      :value="value"
      :disabled="disabled"
      :title="title"
      @change="e => $emit('change', e.target.value)"
    />
  </span>
</template>

<script>
import {
  ref,
  inject,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue'

import { Input as AntInput } from 'ant-design-vue'

export default {
  name: 'JsonExampleInput',
  components: { AntInput },
  props: {
    inputKey: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    disabled: Boolean,
    title: String,
    placeholder: {
      type: String,
      default: 'Example',
    },
  },
  emits: ['change'],
  setup(props) {
    const inputRef = ref(null)
    const pendingFocusKey = inject('pendingExampleFormatFocusKey', ref(null))
    const focusGeneration = inject('exampleFormatFocusGeneration', ref(0))
    const inputRegistry = inject('exampleFormatInputRegistry', null)

    const getNativeInput = () => {
      const inst = inputRef.value
      if (!inst) return null
      const fromExpose = inst.input?.value ?? inst.input
      if (fromExpose?.tagName === 'INPUT' || fromExpose?.tagName === 'TEXTAREA') return fromExpose
      const el = inst.$el ?? inst
      return el?.querySelector?.('input,textarea') ?? null
    }

    const focusInput = () => {
      if (props.disabled) return false
      const inst = inputRef.value
      const nativeInput = getNativeInput()
      if (typeof inst?.focus === 'function') inst.focus({ preventScroll: true })
      if (nativeInput?.focus) {
        nativeInput.focus({ preventScroll: true })
        if (nativeInput.setSelectionRange) {
          const len = nativeInput.value?.length ?? 0
          nativeInput.setSelectionRange(len, len)
        }
      }
      return !!(nativeInput && document.activeElement === nativeInput)
    }

    const tryFocusIfPending = async () => {
      if (pendingFocusKey.value !== props.inputKey) return
      await nextTick()
      focusInput()
    }

    onMounted(() => {
      if (inputRegistry) {
        inputRegistry[props.inputKey] = { focus: focusInput }
      }
      tryFocusIfPending()
    })

    onBeforeUnmount(() => {
      if (inputRegistry?.[props.inputKey]) {
        delete inputRegistry[props.inputKey]
      }
    })

    watch([pendingFocusKey, focusGeneration], tryFocusIfPending, { flush: 'post' })

    return { inputRef }
  },
}
</script>
